import { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Reusable image upload widget. Supports both URL paste AND file upload.
 * Calls onChange(url) when uploaded or pasted.
 */
export default function ImageUpload({ value = "", onChange, label = "Görsel", testId = "image-upload" }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Sadece görsel yükleyebilirsiniz");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Dosya 5MB'tan büyük olamaz");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success("Yüklendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={testId}>
      {label && <div className="text-xs text-gray-600 mb-1">{label}</div>}
      <div className="flex items-stretch gap-2">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Görsel URL veya 'Yükle' ile dosya seçin"
          className="flex-1 h-9 px-3 rounded-md bg-softbg border border-gray-200 text-sm focus:outline-none focus:border-gold"
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="h-9 px-3 rounded-md bg-navy text-white text-xs flex items-center gap-1.5 hover:bg-[#123b52] disabled:opacity-50"
          data-testid={`${testId}-button`}
        >
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          {uploading ? "Yükleniyor..." : "Yükle"}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="h-9 px-2 rounded-md bg-rose-50 text-rose-600 text-xs hover:bg-rose-100"
            data-testid={`${testId}-clear`}
          >
            <X size={14} />
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
      />
      {value && (
        <div className="mt-2 inline-block rounded-md overflow-hidden border border-gray-100">
          <img src={value} alt="" className="h-20 w-auto object-cover bg-softbg" />
        </div>
      )}
    </div>
  );
}
